<template>
  <div class="d-flex flex-column align-items-stretch overflow-hidden h-100">

    <!-- Header bar -->
    <div v-if="showHeader" class="header-bar d-flex flex-row align-items-center justify-content-start border-bottom gap-2 px-2">
      <div class="form-text fw-semibold my-0 flex-grow-1 d-flex flex-row align-items-center">
        <spinner-view small v-if="loading" class="me-2"/>
        <div class="ms-1 me-auto">{{ message }}</div>
        <template v-if="hasPages">
          <IconButton button icon="chevron-left" @click="$emit('previous-page')" compact :disabled="pageStart <= 0" />
          <IconButton button icon="chevron-right" @click="$emit('next-page')" compact :disabled="pageEnd >= count" />
        </template>
      </div>
      <button
        ref="filterBtn"
        type="button"
        :class="['btn btn-flat btn-compact position-relative', { active: showFilters }]"
        @click="toggleFilters"
      >
        <i :class="`bi-${expandIcon || 'sliders2'}`" />
        <span
          v-if="activeFilterCount > 0 && !showFilters"
          class="filter-dot"
        />
      </button>
      <slot name="icons"/>
    </div>

    <!-- Body (list always visible) -->
    <div class="flex-grow-1 overflow-auto">
      <slot name="list" />
    </div>

    <!-- Filter popover (teleported to body for overflow) -->
    <Teleport to="body">
      <div v-if="showFilters" ref="popover" class="filter-popover" :style="popoverStyle">
        <!-- Header -->
        <div class="d-flex flex-row align-items-center justify-content-between px-3 py-2 popover-section">
          <span class="fw-semibold" style="font-size:0.85rem">Sort & Filter</span>
          <button type="button" class="btn btn-flat btn-sm btn-ghost p-0" @click="cancel" style="line-height:1">
            <i class="bi-x-lg" />
          </button>
        </div>

        <!-- Filter content -->
        <div class="filter-popover-body">
          <slot name="filters" />
        </div>

        <!-- Footer -->
        <div class="d-flex flex-row align-items-center justify-content-between px-3 py-2 popover-section border-top">
          <button type="button" class="btn btn-sm btn-link text-dark p-0" @click="reset">Reset all</button>
          <div class="d-flex flex-row gap-2">
            <button type="button" class="btn btn-sm btn-flat" @click="cancel">Cancel</button>
            <button type="button" class="btn btn-sm btn-dark" @click="apply">Apply</button>
          </div>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div v-if="showFilters" class="filter-backdrop" @mousedown="cancel" />
    </Teleport>
  </div>
</template>

<script>
import IconButton from "@/components/common/IconButton.vue";
import SpinnerView from "@/components/common/SpinnerView.vue";

export default {
  components: {SpinnerView, IconButton},
  emits: ['previous-page', 'next-page', 'updated', 'reset', 'canceled'],
  props: {
    noun: String,
    count: Number,
    pageStart: Number,
    pageEnd: Number,
    query: String,
    loading: Boolean,
    expandIcon: String,
    activeFilterCount: {
      type: Number,
      default: 0
    },
    showHeader: {
      type: Boolean,
      default: () => true
    }
  },
  data() {
    return {
      showFilters: false,
      popoverStyle: {}
    };
  },
  computed: {
    hasPages() {
      return this.pageStart >= 0 && this.pageEnd >= this.pageStart && (this.pageStart > 0 || this.pageEnd < this.count);
    },
    message() {
      if (this.loading) {
        return 'Loading...';
      }
      let msg = null;
      if (this.hasPages) {
        msg = `${this.formatNumber(this.pageStart+1)}-${this.formatNumber(this.pageEnd)} of ${this.formatNumber(this.count, this.noun)}`;
      } else {
        msg = this.formatNumber(this.count, this.noun);
      }
      if (this.query) {
        return `Showing ${msg} matching "${this.query}"`;
      } else {
        return msg;
      }
    }
  },
  methods: {
    toggleFilters() {
      if (this.showFilters) {
        this.cancel();
      } else {
        this.positionPopover();
        this.showFilters = true;
        this.$nextTick(() => {
          document.addEventListener('keydown', this.onKeydown);
        });
      }
    },
    positionPopover() {
      const btn = this.$refs.filterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      // Position: right-aligned to the button, below it
      this.popoverStyle = {
        top: `${rect.bottom + 6}px`,
        right: `${window.innerWidth - rect.right - 40}px`
      };
    },
    reset() {
      this.$emit('reset');
    },
    apply() {
      this.$emit('updated');
      this.closePopover();
    },
    cancel() {
      this.$emit('canceled');
      this.closePopover();
    },
    closePopover() {
      this.showFilters = false;
      document.removeEventListener('keydown', this.onKeydown);
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.cancel();
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  }
}
</script>

<style scoped>
.filter-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--bs-dark);
}
.filter-popover {
  position: fixed;
  z-index: 1050;
  width: 300px;
  background: #fff;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}
.filter-popover-body {
  max-height: 55vh;
  overflow-y: auto;
}
.popover-section {
  border-bottom: 1px solid var(--bs-border-color);
}
.popover-section:last-child {
  border-bottom: none;
}
.filter-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1049;
}
</style>
